import type { ElementDefinition } from "cytoscape";
import { getAffinityChartNodeVisualSrc } from "@/features/affinity-chart/affinity-chart-node-visuals";
import {
  buildDefaultAffinityChartFilters,
  createAffinityChartNodeId,
  createEmptyAffinityChartSelection,
  type AffinityChartDetailView,
  type AffinityChartEdge,
  type AffinityChartFilters,
  type AffinityChartLayoutPositions,
  type AffinityChartPosition,
  type AffinityChartSelection,
  type AffinityChartSnapshot,
  type AffinityChartVisibleGraph,
} from "@/features/affinity-chart/affinity-chart.types";

export function sanitizeAffinityChartPositions(
  input: unknown,
  validNodeIds?: Set<string>,
): AffinityChartLayoutPositions {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return {};
  }

  const positions: AffinityChartLayoutPositions = {};

  Object.entries(input as Record<string, unknown>).forEach(([nodeId, value]) => {
    if (validNodeIds && !validNodeIds.has(nodeId)) {
      return;
    }

    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return;
    }

    const x = Number((value as { x?: unknown }).x);
    const y = Number((value as { y?: unknown }).y);

    if (Number.isFinite(x) && Number.isFinite(y)) {
      positions[nodeId] = { x, y };
    }
  });

  return positions;
}

export function createAffinityChartElements(
  nodes: AffinityChartSnapshot["nodes"],
  edges: AffinityChartEdge[],
  positions: AffinityChartLayoutPositions,
) {
  const hasSavedPositions = Object.keys(positions).length > 0;
  const elements: ElementDefinition[] = [
    ...nodes.map((node, index) => {
      const element: ElementDefinition = {
        data: {
          family: node.family,
          id: node.id,
          imageSrc: getAffinityChartNodeVisualSrc(node),
          label: node.label,
          recordKind: node.recordKind,
          type: node.type,
        },
      };

      if (positions[node.id]) {
        element.position = positions[node.id];
      } else if (hasSavedPositions) {
        element.position = {
          x: 140 + (index % 4) * 160,
          y: 100 + Math.floor(index / 4) * 140 + (node.family === "lore-entry" ? 30 : 0),
        };
      }

      return element;
    }),
    ...edges.map((edge) => ({
      data: {
        id: edge.id,
        label: edge.label,
        relationType: edge.relationType,
        source: edge.sourceNodeId,
        target: edge.targetNodeId,
      },
    })),
  ];

  return elements;
}

export function deriveAffinityChartVisibleGraph(
  snapshot: AffinityChartSnapshot,
  filters: AffinityChartFilters = buildDefaultAffinityChartFilters(snapshot.filterOptions),
) {
  const visibleTypes = new Set(filters.visibleTypes);
  const visibleNodes = snapshot.nodes.filter((node) => visibleTypes.has(node.type));
  const visibleNodeIds = new Set(visibleNodes.map((node) => node.id));
  const visibleEdges = snapshot.edges.filter(
    (edge) => visibleNodeIds.has(edge.sourceNodeId) && visibleNodeIds.has(edge.targetNodeId),
  );

  return {
    counts: {
      visibleEdges: visibleEdges.length,
      visibleNodes: visibleNodes.length,
    },
    edges: visibleEdges,
    nodes: visibleNodes,
  } satisfies AffinityChartVisibleGraph;
}

export function clampAffinityChartSelection(
  snapshot: AffinityChartSnapshot,
  filters: AffinityChartFilters,
  selection: AffinityChartSelection,
) {
  if (selection.kind === "none") {
    return selection;
  }

  const visibleGraph = deriveAffinityChartVisibleGraph(snapshot, filters);
  const visibleIds =
    selection.kind === "node"
      ? new Set(visibleGraph.nodes.map((node) => node.id))
      : new Set(visibleGraph.edges.map((edge) => edge.id));

  return visibleIds.has(selection.selectedId)
    ? selection
    : createEmptyAffinityChartSelection();
}

export function resolveAffinityChartDetail(
  snapshot: AffinityChartSnapshot,
  selection: AffinityChartSelection,
) {
  if (selection.kind === "none") {
    return {
      descriptionKey: "affinityChart.detail.emptyBody",
      kind: "empty",
      titleKey: "affinityChart.detail.emptyTitle",
    } satisfies AffinityChartDetailView;
  }

  const nodesById = new Map(snapshot.nodes.map((node) => [node.id, node]));

  if (selection.kind === "node") {
    const node = nodesById.get(selection.selectedId);

    if (!node) {
      return {
        descriptionKey: "affinityChart.detail.unavailableBody",
        kind: "empty",
        titleKey: "affinityChart.detail.unavailableTitle",
      } satisfies AffinityChartDetailView;
    }

    return {
      kind: "node",
      node,
    } satisfies AffinityChartDetailView;
  }

  const edge = snapshot.edges.find((entry) => entry.id === selection.selectedId);
  if (!edge) {
    return {
      descriptionKey: "affinityChart.detail.unavailableBody",
      kind: "empty",
      titleKey: "affinityChart.detail.unavailableTitle",
    } satisfies AffinityChartDetailView;
  }

  const sourceNode = nodesById.get(edge.sourceNodeId);
  const targetNode = nodesById.get(edge.targetNodeId);

  if (!sourceNode || !targetNode) {
    return {
      descriptionKey: "affinityChart.detail.unavailableBody",
      kind: "empty",
      titleKey: "affinityChart.detail.unavailableTitle",
    } satisfies AffinityChartDetailView;
  }

  return {
    edge,
    kind: "edge",
    sourceNode,
    targetNode,
  } satisfies AffinityChartDetailView;
}

export function buildAffinityChartEndpointNodeId(
  kind: "entity" | "lore",
  recordId: string,
) {
  return createAffinityChartNodeId(kind === "entity" ? "entity-card" : "lore-entry", recordId);
}

export function createAffinityChartManualPosition(
  positions: AffinityChartLayoutPositions,
  nodeId: string,
  delta: AffinityChartPosition,
) {
  const current = positions[nodeId];

  if (!current) {
    return positions;
  }

  return {
    ...positions,
    [nodeId]: {
      x: current.x + delta.x,
      y: current.y + delta.y,
    },
  };
}
